import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { Path } from '../../Routing';
import { Link, useRouteMatch } from 'react-router-dom';
import MenuButton from './MenuButton';
import { themeColor } from '../../Theming';
import MenuLink from './MenuLink';
import env from '../../env';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 100%;
`;

const SpaceFiller = styled.div`
  flex: 1;
`;

const SubLink = styled(Link)`
  color: ${themeColor('darkestGrey')};
  text-decoration: none;
  padding: 7.5px 0 7.5px 40px;

  &:hover {
    text-decoration: underline;
  }
`;

const SubLinks = styled.div`
  display: flex;
  flex-direction: column;
  padding: 7.5px 0;
  border-top: 1px solid ${themeColor('lightestGrey')};
`;

interface ExpandingMenuItemProps {
  title: string;
  path: string;
}

const ExpandingMenuItem: React.FC<ExpandingMenuItemProps> = ({
  title,
  path,
  children,
}) => {
  const routeMatch = useRouteMatch(path);
  const [expanded, setExpanded] = useState(routeMatch !== null);

  const handleClick = () => {
    setExpanded(!expanded);
  };

  return (
    <>
      <MenuButton
        onClick={handleClick}
        arrowDirection={expanded ? 'down' : 'right'}
      >
        {title}
      </MenuButton>
      {expanded && <SubLinks>{children}</SubLinks>}
    </>
  );
};

const ProductsLevelLinks: React.FC = () => {
  const { t } = useTranslation();

  return (
    <>
      <SubLink to={Path.ProductHome}>{t('navigation.allProducts')}</SubLink>
      <SubLink to={Path.ProductExtension}>
        {t('navigation.mobileExtension')}
      </SubLink>
      <SubLink to={Path.ProductDesktop}>
        {t('navigation.desktopExtension')}
      </SubLink>
      <SubLink to={Path.ProductApi}>{t('navigation.dealsApi')}</SubLink>
    </>
  );
};

const BenefitsLevelLinks: React.FC = () => {
  const { t } = useTranslation();

  return (
    <>
      <SubLink to={Path.Revenue}>{t('footer.monetization')}</SubLink>
      <SubLink to={Path.Loyalty}>{t('navigation.loyalty')}</SubLink>
      <SubLink to={Path.ESG}>{t('navigation.esg')}</SubLink>
    </>
  );
};

const AboutKindredLevelLinks: React.FC = () => {
  const { t } = useTranslation();

  return (
    <>
      <SubLink to={Path.AboutUs}>{t('navigation.aboutKindred')}</SubLink>
      <SubLink to={Path.Network}>{t('navigation.ourNetwork')}</SubLink>
    </>
  );
};

const RootLevelLinks: React.FC = () => {
  const { t } = useTranslation();

  return (
    <>
      <ExpandingMenuItem path={Path.Home} title={t('navigation.products')}>
        <ProductsLevelLinks />
      </ExpandingMenuItem>
      <ExpandingMenuItem path={Path.Home} title={t('navigation.benefits')}>
        <BenefitsLevelLinks />
      </ExpandingMenuItem>
      <MenuLink to={Path.Gaming}>{t('navigation.gaming')}</MenuLink>
      <ExpandingMenuItem
        path={Path.AboutUs}
        title={t('navigation.aboutKindred')}
      >
        <AboutKindredLevelLinks />
      </ExpandingMenuItem>
      <MenuLink to={{ pathname: env.blog_link }} target="_blank">
        {t('navigation.blog')}
      </MenuLink>
    </>
  );
};

const MenuBody: React.FC = () => {
  return (
    <Container>
      <RootLevelLinks />
      <SpaceFiller />
    </Container>
  );
};

export default MenuBody;
